import { useEffect, useState } from "react";

/**
 * A breakpoint is only usable if it is a positive, finite pixel width; every
 * other value (including the attribute being absent) disables the query.
 */
function toBreakpoint(maxWidth: number | undefined): number | null {
  return typeof maxWidth === "number" && Number.isFinite(maxWidth) && maxWidth > 0 ? maxWidth : null;
}

/**
 * `max-width` is inclusive, so the breakpoint itself must fall on the wide
 * side of the query: a 992px breakpoint means "narrower than 992px".
 */
function mediaQuery(breakpoint: number): string {
  return `(max-width: ${breakpoint - 0.02}px)`;
}

function matches(maxWidth: number | undefined): boolean {
  const breakpoint = toBreakpoint(maxWidth);
  if (breakpoint === null || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(mediaQuery(breakpoint)).matches;
}

/**
 * Whether the browser viewport is narrower than `maxWidth` pixels.
 *
 * Deliberately a viewport measurement, against the grain of the widget's
 * container-query layout (see `gc-app-container` in `BookingFormElement`): the
 * launcher bar this drives is fixed to the browser window and replaces the
 * widget on the embedding page, so the decision belongs to the window rather
 * than to the widget's own column. Layout *inside* the widget must keep using
 * container queries.
 */
export function useViewportNarrowerThan(maxWidth: number | undefined): boolean {
  const [isNarrower, setIsNarrower] = useState(() => matches(maxWidth));

  useEffect(() => {
    const breakpoint = toBreakpoint(maxWidth);
    if (breakpoint === null || typeof window.matchMedia !== "function") {
      setIsNarrower(false);
      return;
    }

    const query = window.matchMedia(mediaQuery(breakpoint));
    const onChange = (event: MediaQueryListEvent) => setIsNarrower(event.matches);

    setIsNarrower(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, [maxWidth]);

  return isNarrower;
}
